import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {boolean} */
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    const gallery = this.querySelector('.dialog-zoomed-gallery');

    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('cancel', this.handleCancel);
    gallery?.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const gallery = this.querySelector('.dialog-zoomed-gallery');

    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('cancel', this.handleCancel);
    gallery?.removeEventListener('scroll', this.handleScroll);
    this.handleScroll.cancel();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The pointer event.
   */
  async open(index, event) {
    event?.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];

    if (!targetImage) return;

    const targetThumbnail = thumbnails.children[index];

    const openDialog = () => {
      if (!dialog.open) {
        dialog.showModal();
      }

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event?.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage) {
      openDialog();
      await this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const itemTransitionName = 'gallery-item-open';
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    try {
      await startViewTransition(() => {
        openDialog();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty(
          'view-transition-name',
          itemTransitionName
        );
      });
    } catch {
      openDialog();
    } finally {
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.removeProperty('view-transition-name');
      document.documentElement.style.removeProperty(
        '--gallery-media-focal-point'
      );
    }

    await this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    // Mark immediately to prevent duplicate requests.
    this.#highResImagesLoaded.add(highResolutionUrl);

    const highResolutionImage = new Image();
    highResolutionImage.decoding = 'async';

    highResolutionImage.onload = () => {
      if (!image.isConnected) return;

      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = highResolutionUrl;
    };

    highResolutionImage.onerror = () => {
      // Allow a later retry if the request fails.
      this.#highResImagesLoaded.delete(highResolutionUrl);
    };

    highResolutionImage.src = highResolutionUrl;
    return true;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    if (!media.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);

    if (!this.isConnected) return;

    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex < 0) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-pressed', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;

    try {
      if (!supportsViewTransitions() || isLowPowerDevice() || !media.length) {
        this.closeDialog();
        return;
      }

      const mostVisibleElement = await getMostVisibleElement(media);
      const activeIndex = media.indexOf(mostVisibleElement);

      if (activeIndex < 0) {
        this.closeDialog();
        return;
      }

      const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest('media-gallery')
      );

      const slideshowActive = mediaGallery?.presentation === 'carousel';
      const slide = slideshowActive
        ? mediaGallery.slideshow?.slides?.[activeIndex]
        : mediaGallery?.media?.[activeIndex];

      if (!(slide instanceof HTMLElement)) {
        this.closeDialog();
        return;
      }

      const itemTransitionName = 'gallery-item-close';
      const focalPoint = slide.dataset.focalPoint;

      if (focalPoint) {
        document.documentElement.style.setProperty(
          '--gallery-media-focal-point',
          focalPoint
        );
      }

      dialog.classList.add('dialog--closed');

      try {
        await onAnimationEnd(this.refs.thumbnails);

        if (!dialog.open) return;

        mostVisibleElement.style.setProperty(
          'view-transition-name',
          itemTransitionName
        );

        await startViewTransition(() => {
          mostVisibleElement.style.removeProperty('view-transition-name');
          slide.style.setProperty(
            'view-transition-name',
            itemTransitionName
          );
          this.closeDialog();
        });
      } finally {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.removeProperty('view-transition-name');
        dialog.classList.remove('dialog--closed');
        document.documentElement.style.removeProperty(
          '--gallery-media-focal-point'
        );
      }
    } catch {
      this.closeDialog();
    } finally {
      this.#isClosing = false;
    }
  }

  closeDialog() {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles native dialog cancellation such as Android back navigation.
   *
   * @param {Event} event - The cancel event.
   */
  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;

    if (isNaN(index) || index < 0 || index >= media.length) return;

    const targetImage = media[index];
    if (!targetImage) return;

    const targetThumbnail = thumbnails.children[index];

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-pressed', `${i === index}`);
    });

    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    targetImage.scrollIntoView({
      behavior: options.behavior,
    });

    this.loadHighResolutionImage(targetImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
